import type { CanvasPoint, CanvasTapTargets, SpinPhaseStore } from "@slot/game-shell";
import type { PresentationTicker } from "@slot/presentation";
import type { ChefPort } from "./ChefView";

/** The pacing of the chef's reactions (engine seconds). */
export interface ChefTuning {
  /** A win's cheer at most once in this long: a cascade's wins come quicker than his cheer plays. */
  winCooldownSeconds: number;
}

/** The WIN readout's amount as the round grows it (the shell's current win store). */
interface WinReadout {
  getSnapshot(): { currentWinMinor: string };
  subscribe(listener: (snapshot: { currentWinMinor: string }) => void): () => void;
}

/** How many panels are open over the game (the shell's canvas overlay store). */
interface OpenPanels {
  getSnapshot(): { open: number };
}

/**
 * The chef follows the round: he spins along while a spin is in flight (the spin's phase, from the request to the
 * settlement), cheers a win as the WIN readout grows (at most once a cooldown, so a cascade's wins do not restart his
 * cheer), celebrates a big win and a free-spins award (their panels' entry calls `celebrate`), and greets a tap on him
 * while the game is idle with no panel open — a tap during a spin stays the slam's.
 */
export class ChefPresenter {
  private lastWinMinor = 0n;
  private lastCheerAt = Number.NEGATIVE_INFINITY;

  public constructor(
    private readonly deps: {
      chef: ChefPort;
      spinPhase: SpinPhaseStore;
      currentWin: WinReadout;
      overlays: OpenPanels;
      taps: CanvasTapTargets;
      /** Engine time: the cooldown runs on it. */
      clock: PresentationTicker;
      tuning: ChefTuning;
    },
  ) {}

  /** Starts following the round; returns the stop. */
  public start(): () => void {
    const { chef, spinPhase, currentWin, taps } = this.deps;
    this.lastWinMinor = BigInt(currentWin.getSnapshot().currentWinMinor);
    chef.setSpinning(spinPhase.getPhase() !== "idle");
    const stops = [
      spinPhase.subscribe((phase) => chef.setSpinning(phase !== "idle")),
      currentWin.subscribe((snapshot) => this.onWin(BigInt(snapshot.currentWinMinor))),
      taps.add((point) => this.onTap(point)),
    ];
    return () => {
      for (const stop of stops) stop();
    };
  }

  /** A big win or a free-spins award: his big celebration, over whatever he plays. */
  public celebrate(): void {
    this.lastCheerAt = this.deps.clock.elapsedSeconds;
    this.deps.chef.react("bigWin");
  }

  private onWin(winMinor: bigint): void {
    const grew = winMinor > this.lastWinMinor;
    this.lastWinMinor = winMinor;
    if (!grew || this.deps.spinPhase.getPhase() === "idle") return;
    const now = this.deps.clock.elapsedSeconds;
    if (now - this.lastCheerAt < this.deps.tuning.winCooldownSeconds) return;
    this.lastCheerAt = now;
    this.deps.chef.react("win");
  }

  private onTap(point: CanvasPoint): boolean {
    if (this.deps.spinPhase.getPhase() !== "idle" || this.deps.overlays.getSnapshot().open > 0 || !this.deps.chef.hits(point)) return false;
    this.deps.chef.react("greet");
    return true;
  }
}
